import React, {useState, useRef, useEffect} from 'react';

const ROSTER = ['Camiel', 'Ed', 'Hans', 'Jim', 'Joris', 'Nina', 'Pieter'];

function Game({date, teams, score, isCurrent, isPast, isAdmin, scoreDict, gamesPlayedDict, onScoreUpdate, onPlayerUpdate}) {
    const [isOpen, setIsOpen] = useState(false);
    const [editingPlayer, setEditingPlayer] = useState(null);
    const [customName, setCustomName] = useState('');
    const [showCustomInput, setShowCustomInput] = useState(false);
    const dropdownRef = useRef(null);
    const customInputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setEditingPlayer(null);
                setShowCustomInput(false);
                setCustomName('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus custom input when shown
    useEffect(() => {
        if (showCustomInput && customInputRef.current) {
            customInputRef.current.focus();
        }
    }, [showCustomInput]);

    const formatName = (name) => {
        const split = name.split("(")
        if (split.length > 1) {
            return (
                <div style={{display: "flex", flexDirection: "row", paddingRight: "5px", paddingLeft: "5px"}}>
                    <strike>{split[0]}</strike>
                    <div>({split[1]})</div>
                </div>
            )
        }
        return <>{name}</>
    }

    const handleIncrement = (e, playerIndex) => {
        e.stopPropagation();
        onScoreUpdate(playerIndex, score[playerIndex] + 1);
    };

    const handleDecrement = (e, playerIndex) => {
        e.stopPropagation();
        onScoreUpdate(playerIndex, Math.max(0, score[playerIndex] - 1));
    };

    const handleNameClick = (e, playerIndex) => {
        e.stopPropagation();
        if (isAdmin) {
            setEditingPlayer(playerIndex);
            setShowCustomInput(false);
            setCustomName('');
        }
    };

    const handlePlayerSelect = (e, playerIndex, newName) => {
        e.stopPropagation();
        if (newName === '__custom__') {
            setShowCustomInput(true);
        } else {
            onPlayerUpdate(playerIndex, newName);
            setEditingPlayer(null);
        }
    };

    const handleCustomSubmit = (e, playerIndex) => {
        e.preventDefault();
        e.stopPropagation();
        if (customName.trim()) {
            onPlayerUpdate(playerIndex, customName.trim());
            setEditingPlayer(null);
            setShowCustomInput(false);
            setCustomName('');
        }
    };

    const renderScoreWithButtons = (playerIndex) => {
        if (isAdmin) {
            return (
                <div className="score-controls">
                    <button
                        className="score-button"
                        onClick={(e) => handleDecrement(e, playerIndex)}
                    >
                        -
                    </button>
                    <span className="score-value">{score[playerIndex]}</span>
                    <button
                        className="score-button"
                        onClick={(e) => handleIncrement(e, playerIndex)}
                    >
                        +
                    </button>
                </div>
            );
        }
        return <span>{score[playerIndex]}</span>;
    };

    const renderPlayerName = (playerIndex) => {
        const currentName = teams[playerIndex];
        const isEditing = editingPlayer === playerIndex;

        if (isAdmin) {
            return (
                <div className="player-name-wrapper" ref={isEditing ? dropdownRef : null}>
                    <span
                        className="player-name editable"
                        onClick={(e) => handleNameClick(e, playerIndex)}
                    >
                        {formatName(currentName)}
                    </span>
                    {isEditing && (
                        <div className="player-dropdown">
                            {!showCustomInput ? (
                                <>
                                    {ROSTER.map((name) => (
                                        <div
                                            key={name}
                                            className={`dropdown-item ${name === currentName ? 'current' : ''}`}
                                            onClick={(e) => handlePlayerSelect(e, playerIndex, name)}
                                        >
                                            {name}
                                        </div>
                                    ))}
                                    <div
                                        className="dropdown-item other"
                                        onClick={(e) => handlePlayerSelect(e, playerIndex, '__custom__')}
                                    >
                                        Anders...
                                    </div>
                                </>
                            ) : (
                                <form onSubmit={(e) => handleCustomSubmit(e, playerIndex)} className="custom-name-form">
                                    <input
                                        ref={customInputRef}
                                        type="text"
                                        value={customName}
                                        onChange={(e) => setCustomName(e.target.value)}
                                        placeholder="Naam..."
                                        className="custom-name-input"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <button type="submit" className="custom-name-submit">OK</button>
                                </form>
                            )}
                        </div>
                    )}
                </div>
            );
        }
        return <span>{formatName(currentName)}</span>;
    };

    // Compute team pairings: best+worst vs 2nd+3rd based on scoreboard
    const getOrderedTeams = () => {
        if (teams.length !== 4 || !scoreDict) return null;

        const getName = (entry) => entry.split("(")[0].trim();

        const players = teams.map((team, idx) => ({
            idx,
            name: getName(team),
            fullName: team,
        }));

        // Sort: highest score first; tiebreak: fewer games played = better
        players.sort((a, b) => {
            const scoreA = scoreDict[a.name] || 0;
            const scoreB = scoreDict[b.name] || 0;
            if (scoreB !== scoreA) return scoreB - scoreA;
            const gamesA = gamesPlayedDict[a.name] || 0;
            const gamesB = gamesPlayedDict[b.name] || 0;
            return gamesA - gamesB;
        });

        // Best(0) + Worst(3) = Team 1, 2nd(1) + 3rd(2) = Team 2
        return {
            team1: [players[0], players[3]],
            team2: [players[1], players[2]],
        };
    };

    const orderedTeams = getOrderedTeams();

    return (
        <div className={`game ${isCurrent ? 'current-game' : ''}`}>
            <div className="game-info" onClick={() => setIsOpen(!isOpen)}>
                <span className="game-date">{date}</span>
                <span className="game-teams">
                    {orderedTeams ? (
                        <>
                            {formatName(orderedTeams.team1[0].fullName)} {formatName(orderedTeams.team1[1].fullName)}
                            <span className="vs-separator">VS</span>
                            {formatName(orderedTeams.team2[0].fullName)} {formatName(orderedTeams.team2[1].fullName)}
                        </>
                    ) : (
                        <>
                            {formatName(teams[0])} vs {formatName(teams[1])} vs {formatName(teams[2])} vs {formatName(teams[3])} {teams.length > 4 && "vs"} {teams.length > 4 && formatName(teams[4])}
                        </>
                    )}
                </span>
            </div>
            {isOpen && (
                <div className="game-scores">
                    {orderedTeams ? (
                        [orderedTeams.team1[0], orderedTeams.team1[1], orderedTeams.team2[0], orderedTeams.team2[1]].map((player) => (
                            <div className="score-team" key={player.idx}>
                                {renderPlayerName(player.idx)}
                                {renderScoreWithButtons(player.idx)}
                            </div>
                        ))
                    ) : (
                        <>
                            {teams.map((_, idx) => (
                                <div className="score-team" key={idx}>
                                    {renderPlayerName(idx)}
                                    {renderScoreWithButtons(idx)}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default Game;
